import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { KeyIcon, EyeIcon, EyeOffIcon, ArrowLeftIcon } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getCourier } from '../../state/courierSlice';
import { usePasswordReset } from '../hooks';
import { showErrorToast } from '../../utils/toast';

const ResetPassword = () => {
  const { accessToken } = useParams<{ accessToken: string }>();
  const navigate = useNavigate();
  const courier = useSelector(getCourier);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);

  const { resetPassword, isLoading } = usePasswordReset();

  useEffect(() => {
    if (!accessToken) {
      setIsValidToken(false);
      showErrorToast({
        title: 'Invalid Reset Link',
        message: 'The password reset link is invalid or has expired.'
      });
    }
  }, [accessToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!accessToken) return;

    const result = await resetPassword(accessToken, password, confirmPassword);
    if (result.success) {
      // Redirect to login page after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    }
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  if (!isValidToken) {
    return (
      <div className="flex flex-col md:flex-row w-full min-h-screen bg-gray-50">
        {/* Branding Section */}
        <div
          className="w-full md:w-1/2 flex flex-col items-center justify-center p-8 text-white"
          style={{ backgroundColor: courier?.themeSetting?.primaryColor || '#2196f3' }}
        >
          <div className="max-w-md text-center">
            {courier?.imageUrl && (
              <div className="mb-4 flex justify-center">
                <div className="bg-white rounded-full p-4 w-24 h-24 flex items-center justify-center">
                  <img
                    src={courier.imageUrl}
                    alt={`${courier.name} logo`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              </div>
            )}
            <h1 className="text-3xl font-bold mb-2">{courier?.name}</h1>
            {courier?.slogan && <p className="text-lg opacity-90">{courier.slogan}</p>}
          </div>
        </div>

        {/* Form Section */}
        <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <svg className="h-8 w-8 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-4">Invalid Reset Link</h2>
            <p className="text-gray-600 mb-6">
              The password reset link is invalid or has expired. Please request a new password reset link.
            </p>
            <button
              type="button"
              onClick={handleBackToLogin}
              className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              style={{ backgroundColor: courier?.themeSetting?.primaryColor || '#2196f3' }}
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row w-full min-h-screen bg-gray-50">
      {/* Branding Section */}
      <div
        className="w-full md:w-1/2 flex flex-col items-center justify-center p-8 text-white"
        style={{ backgroundColor: courier?.themeSetting?.primaryColor || '#2196f3' }}
      >
        <div className="max-w-md text-center">
          {courier?.imageUrl && (
            <div className="mb-4 flex justify-center">
              <div className="bg-white rounded-full p-4 w-24 h-24 flex items-center justify-center">
                <img
                  src={courier.imageUrl}
                  alt={`${courier.name} logo`}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
          )}
          <h1 className="text-3xl font-bold mb-2">{courier?.name}</h1>
          {courier?.slogan && <p className="text-lg opacity-90">{courier.slogan}</p>}
        </div>
      </div>

      {/* Form Section */}
      <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-md">
          <button
            type="button"
            onClick={handleBackToLogin}
            className="mb-4 flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to login
          </button>

          <h2 className="text-2xl font-bold mb-6 text-center">
            Reset Your Password
          </h2>

          <p className="text-gray-600 mb-6 text-center">
            Enter your new password below to complete the password reset process.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm your new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showConfirmPassword ? (
                    <EyeOffIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              style={{ backgroundColor: courier?.themeSetting?.primaryColor || '#2196f3' }}
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              ) : null}
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
